import React, { useEffect, useState } from "react"
import { IconX } from "@tabler/icons-react"
import LandingButton from "./LandingButton"
import apiClient from "../../lib/api"

const inputClassName =
  "w-full rounded-xl border border-[#d9d9d9] bg-[#fafafa] px-2.5 py-3 font-dm-sans text-base tracking-wide text-[#0d0d0d] placeholder:text-[#787878] outline-none focus:border-dc-primary-900"

const labelClassName = "font-dm-sans text-sm leading-[22px] tracking-wide text-[#2e2e2e]"

type Props = {
  ministries: string[]
  initialMinistry?: string
  open: boolean
  onClose: () => void
}

const LandingMinistryJoinModal: React.FC<Props> = ({
  ministries,
  initialMinistry = "",
  open,
  onClose,
}) => {
  const [formData, setFormData] = useState({
    ministry: "",
    name: "",
    email: "",
    phone: "",
    message: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle")

  useEffect(() => {
    if (!open) return

    setSubmitStatus("idle")
    setFormData({
      ministry: initialMinistry,
      name: "",
      email: "",
      phone: "",
      message: "",
    })

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }

    document.addEventListener("keydown", handleEscape)
    document.body.style.overflow = "hidden"

    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = ""
    }
  }, [open, onClose, initialMinistry])

  if (!open) return null

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.ministry || !formData.name || !formData.email) {
      setSubmitStatus("error")
      return
    }

    setIsSubmitting(true)
    setSubmitStatus("idle")

    try {
      await apiClient.createMinistryJoinRequest({
        ministry: formData.ministry,
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        message: formData.message || undefined,
      })
      setSubmitStatus("success")
      setFormData({ ministry: "", name: "", email: "", phone: "", message: "" })
    } catch (err) {
      console.error("Error submitting ministry join request:", err)
      setSubmitStatus("error")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ministry-join-title"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-[0px_2px_12px_0px_rgba(0,0,0,0.1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 flex size-9 items-center justify-center rounded-full text-[#4a4a4a] transition-colors hover:bg-[#f5f5f5]"
        >
          <IconX size={20} stroke={1.5} />
        </button>

        <form onSubmit={handleSubmit} className="flex flex-col gap-7 px-6 py-8">
          <header className="flex flex-col gap-2 pr-8">
            <h2 id="ministry-join-title" className="font-outfit text-[28px] font-semibold leading-tight text-[#0d0d0d]">
              Join a Ministry
            </h2>
            <p className="font-dm-sans text-base leading-6 tracking-wide text-[#2e2e2e]">
              Tell us a little about yourself and we&apos;ll be in touch.
            </p>
          </header>

          <div className="flex flex-col gap-[18px]">
            <div className="flex flex-col gap-2">
              <label htmlFor="ministry-join-ministry" className={labelClassName}>
                Ministry
              </label>
              <select
                id="ministry-join-ministry"
                name="ministry"
                value={formData.ministry}
                onChange={handleChange}
                className={inputClassName}
                required
              >
                <option value="">Select a ministry</option>
                {ministries.map((ministry) => (
                  <option key={ministry} value={ministry}>
                    {ministry}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="ministry-join-name" className={labelClassName}>
                Name
              </label>
              <input
                id="ministry-join-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                className={inputClassName}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="ministry-join-email" className={labelClassName}>
                Email
              </label>
              <input
                id="ministry-join-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                className={inputClassName}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="ministry-join-phone" className={labelClassName}>
                Phone <span className="text-[#787878]">(optional)</span>
              </label>
              <input
                id="ministry-join-phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+353 ..."
                className={inputClassName}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="ministry-join-message" className={labelClassName}>
                Message <span className="text-[#787878]">(optional)</span>
              </label>
              <textarea
                id="ministry-join-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Why would you like to join this group?"
                rows={4}
                className={`${inputClassName} resize-none`}
              />
            </div>
          </div>

          {submitStatus === "success" && (
            <p className="font-dm-sans text-sm text-dc-primary-900">
              Thank you! Your request has been submitted. We&apos;ll be in touch soon.
            </p>
          )}

          {submitStatus === "error" && (
            <p className="font-dm-sans text-sm text-error">
              Please fill in all required fields and try again.
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            <LandingButton type="submit" className="min-w-[120px] disabled:opacity-60" disabled={isSubmitting}>
              {isSubmitting ? "Submitting…" : "Submit request"}
            </LandingButton>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-w-[100px] items-center justify-center rounded-xl border border-[#d9d9d9] px-3 py-3.5 font-dm-sans text-base font-semibold tracking-wide text-[#0d0d0d] transition-opacity hover:opacity-80"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default LandingMinistryJoinModal
